import { useState, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useToast } from '../../ui/Toast';
import { PageHead } from '../../shell/PageHead';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';

const card: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-lg)',
  background: 'var(--bg-1)',
  padding: 22,
  maxWidth: 520
};

export function Account() {
  const { user, apiFetch, refresh } = useAuth();
  const toast = useToast();

  // --- senha ---
  const [curPwd, setCurPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confPwd, setConfPwd] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdErr, setPwdErr] = useState<string | null>(null);

  // --- email ---
  const [emailPwd, setEmailPwd] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailErr, setEmailErr] = useState<string | null>(null);

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    if (pwdBusy) return;
    setPwdErr(null);
    if (newPwd.length < 8) {
      setPwdErr('A nova senha deve ter ao menos 8 caracteres.');
      return;
    }
    if (newPwd !== confPwd) {
      setPwdErr('A confirmação não confere com a nova senha.');
      return;
    }
    setPwdBusy(true);
    const r = await apiFetch<{ ok: true }>('/v1/admin/auth/password', {
      method: 'PATCH',
      body: { current_password: curPwd, new_password: newPwd }
    });
    setPwdBusy(false);
    if (!r.ok) {
      setPwdErr(r.error?.message ?? 'Falha ao trocar a senha.');
      return;
    }
    setCurPwd('');
    setNewPwd('');
    setConfPwd('');
    toast('Senha alterada. As outras sessões foram encerradas.', 'success');
  }

  async function onChangeEmail(e: FormEvent) {
    e.preventDefault();
    if (emailBusy) return;
    setEmailErr(null);
    setEmailBusy(true);
    const r = await apiFetch<{ ok: true; email: string }>('/v1/admin/auth/email', {
      method: 'PATCH',
      body: { current_password: emailPwd, new_email: newEmail }
    });
    setEmailBusy(false);
    if (!r.ok) {
      setEmailErr(r.error?.message ?? 'Falha ao trocar o email.');
      return;
    }
    setEmailPwd('');
    setNewEmail('');
    await refresh();
    toast('Email atualizado.', 'success');
  }

  return (
    <div>
      <PageHead
        eyebrow="Conta"
        title="Minha conta"
        desc="Gerencie suas credenciais de acesso ao console."
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 24,
          padding: '12px 16px',
          border: '1px solid var(--border-soft)',
          borderRadius: 'var(--r-md)',
          background: 'var(--bg-2)',
          maxWidth: 520
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 99,
            background: 'linear-gradient(135deg, var(--amber), var(--amber-strong))',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 14
          }}
        >
          {(user?.nome?.[0] ?? 'A').toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{user?.nome}</div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>{user?.email}</div>
        </div>
        <Badge color={user?.papel === 'super_admin' ? 'violet' : 'info'} size="sm">
          {user?.papel === 'super_admin' ? 'super admin' : 'empresa'}
        </Badge>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Trocar senha */}
        <form style={card} onSubmit={onChangePassword}>
          <h3 style={{ fontSize: 15.5, marginBottom: 4 }}>Trocar senha</h3>
          <p style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 16 }}>
            Por segurança, as demais sessões são encerradas ao trocar a senha.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Senha atual" required>
              <Input
                type="password"
                autoComplete="current-password"
                value={curPwd}
                onChange={(e) => setCurPwd(e.target.value)}
              />
            </Field>
            <Field label="Nova senha" hint="Mínimo 8 caracteres." required>
              <Input
                type="password"
                autoComplete="new-password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
              />
            </Field>
            <Field label="Confirmar nova senha" required error={pwdErr ?? undefined}>
              <Input
                type="password"
                autoComplete="new-password"
                value={confPwd}
                onChange={(e) => setConfPwd(e.target.value)}
              />
            </Field>
            <div>
              <Button
                type="submit"
                variant="brand"
                icon="key"
                disabled={pwdBusy || !curPwd || !newPwd || !confPwd}
              >
                {pwdBusy ? 'Salvando…' : 'Trocar senha'}
              </Button>
            </div>
          </div>
        </form>

        {/* Trocar email */}
        <form style={card} onSubmit={onChangeEmail}>
          <h3 style={{ fontSize: 15.5, marginBottom: 4 }}>Trocar email</h3>
          <p style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 16 }}>
            É o email usado para entrar no console.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Novo email" required>
              <Input
                type="email"
                autoComplete="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={user?.email}
              />
            </Field>
            <Field label="Senha atual (confirmação)" required error={emailErr ?? undefined}>
              <Input
                type="password"
                autoComplete="current-password"
                value={emailPwd}
                onChange={(e) => setEmailPwd(e.target.value)}
              />
            </Field>
            <div>
              <Button
                type="submit"
                variant="brand"
                icon="check"
                disabled={emailBusy || !emailPwd || !newEmail}
              >
                {emailBusy ? 'Salvando…' : 'Trocar email'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
